/**
 * Słownik tłumaczeń interfejsu: polski -> angielski.
 *
 * Kluczem jest polski tekst, a nie sztuczny identyfikator. Polski jest
 * językiem źródłowym sklepu, więc:
 *  - w kodzie widać od razu, co się wyświetli, bez zaglądania do katalogu,
 *  - brak wpisu degraduje się do poprawnego polskiego, nie do gołego klucza,
 *  - nie trzeba utrzymywać osobnego katalogu PL.
 *
 * Konsekwencja: zmiana polskiego tekstu w komponencie wymaga zmiany klucza
 * tutaj. Dlatego przy edycji tekstów sprawdź `npm run check:i18n`
 * (skrypt scripts/check-i18n.mjs wypisuje klucze bez pokrycia).
 */
export const EN: Record<string, string> = {
  // --- nawigacja i układ ---
  "Strona główna": "Home",
  Sklep: "Store",
  Konto: "Account",
  Koszyk: "Cart",
  Menu: "Menu",
  "Dostawa do:": "Shipping to:",
  "Wszelkie prawa zastrzeżone.": "All rights reserved.",
  "Zamknij menu": "Close menu",

  // --- strona główna ---
  "Odzież i gadżety dla ateistów": "Clothing and gifts for atheists",
  "Zobacz projekty": "Browse designs",
  "Nasze projekty": "Our designs",
  "Koszulki z autorskimi nadrukami. Rozmiary S–XXL.":
    "T-shirts with original prints. Sizes S–XXL.",
  "Zobacz wszystkie": "View all",

  // --- lista produktów ---
  "Wszystkie produkty": "All products",
  Sortowanie: "Sort by",
  Najnowsze: "Latest arrivals",
  "Cena: od najniższej": "Price: low to high",
  "Cena: od najwyższej": "Price: high to low",
  Kategorie: "Categories",

  // --- produkt ---
  "Wybierz rozmiar": "Select size",
  "Wybierz kolor": "Select colour",
  "Wybierz kolor i rozmiar": "Select colour and size",
  "Kliknij, aby zobaczyć samą grafikę": "Click to see the artwork alone",

  // --- kolory koszulek (wartości opcji "Kolor" w Medusie) ---
  Kolor: "Colour",
  Biały: "White",
  Czarny: "Black",
  Niebieski: "Royal blue",
  Błękitny: "Sky blue",
  Zielony: "Green",
  Czerwony: "Red",
  "Dodaj do koszyka": "Add to cart",
  "Brak na stanie": "Out of stock",
  "Kliknij, aby powiększyć": "Click to enlarge",
  "Podgląd grafiki w pełnym rozmiarze": "Full-size artwork preview",
  "Zamknij podgląd": "Close preview",
  "Poprzednia grafika": "Previous image",
  "Następna grafika": "Next image",
  "Informacje o produkcie": "Product information",
  "Dostawa i zwroty": "Shipping & returns",
  Materiał: "Material",
  Rodzaj: "Type",
  Waga: "Weight",
  Wymiary: "Dimensions",
  "Wysyłka w 1-2 dni": "Ships in 1-2 days",
  "Paczkę nadajemy w ciągu 1-2 dni roboczych od zaksięgowania wpłaty. Do wyboru kurier albo Paczkomat InPost.":
    "We ship within 1-2 business days of payment clearing. Choose courier or an InPost parcel locker.",
  "Wymiana rozmiaru": "Size exchange",
  "Rozmiar nie trafiony? Napisz do nas — wymienimy koszulkę na inną, o ile nie była noszona i ma metki.":
    "Wrong size? Write to us — we will exchange the shirt as long as it is unworn and still tagged.",
  "14 dni na zwrot": "14-day returns",
  "Masz 14 dni na odstąpienie od umowy bez podawania przyczyny. Odsyłasz produkt, my zwracamy pieniądze.":
    "You have 14 days to withdraw from the purchase, no reason needed. Send the item back and we refund you.",
  od: "from",
  "Cena poprzednia:": "Previous price:",
  "Podobne projekty": "Related products",
  "Zobacz też inne nasze nadruki.": "Take a look at our other prints.",

  // --- specyfikacja koszulki ---
  Marka: "Brand",
  Model: "Model",
  Gramatura: "Fabric weight",
  Przędza: "Yarn",
  Krój: "Fit",
  Dekolt: "Neckline",
  Rozmiary: "Sizes",
  "Pochodzenie koszulki": "Shirt made in",
  "Miejsce nadruku": "Printed in",
  Maroko: "Morocco",
  Polska: "Poland",
  Pielęgnacja: "Care",
  "100% bawełna": "100% cotton",
  "klasyczny, bez szwów bocznych": "classic, tubular (no side seams)",
  "ściągacz bawełniany z dodatkiem Lycry®": "cotton rib with Lycra®",
  "Pranie 40°C, nie wybielać, suszarka w niskiej temperaturze, prasowanie średnie, nie prać chemicznie":
    "Machine wash 40°C, do not bleach, tumble dry low, medium iron, do not dry clean",
  "Koszulkę z nadrukiem pierz na lewej stronie i nie prasuj po nadruku.":
    "Wash printed shirts inside out and do not iron over the print.",

  // --- konto i przenoszenie zamówień ---
  Cześć: "Hi",
  Przegląd: "Overview",
  "Zalogowano jako:": "Signed in as:",
  uzupełnione: "completed",
  zapisane: "saved",
  Kwota: "Total amount",
  "Przejdź do zamówienia": "Go to order",
  więcej: "more",
  "Zobacz szczegóły": "See details",
  "Zapisz zmiany": "Save changes",
  "Obsługa klienta": "Customer service",
  "Najczęstsze pytania i odpowiedzi znajdziesz na stronie obsługi klienta.":
    "You can find frequently asked questions and answers on our customer service page.",
  "Wróć do zakupów": "Continue shopping",
  "Podejrzyj i zmień swoje dane: imię, nazwisko, e-mail i telefon. Możesz też zaktualizować adres do faktury albo zmienić hasło.":
    "View and update your details: name, email and phone. You can also update your billing address or change your password.",
  "Zarządzaj adresami dostawy — możesz zapisać ich dowolnie wiele. Zapisane adresy podpowiedzą się przy składaniu zamówienia.":
    "Manage your shipping addresses — save as many as you like. Saved addresses are offered during checkout.",
  "Podejrzyj swoje wcześniejsze zamówienia i ich status. Stąd zgłosisz też zwrot lub wymianę.":
    "View your previous orders and their status. You can also request a return or exchange here.",
  "Przeniesienie zamówienia": "Order transfers",
  "Nie widzisz swojego zamówienia?": "Can't find the order you are looking for?",
  "Przypisz je do swojego konta.": "Connect an order to your account.",
  "Poproś o przeniesienie": "Request transfer",
  "Zgłoszono przeniesienie zamówienia": "Transfer requested for order",
  "Wiadomość z prośbą wysłana na": "Transfer request email sent to",
  "Zamówienie zostało przeniesione.": "Order transferred successfully.",
  "Przeniesienie zamówienia odrzucone.": "Order transfer declined.",
  "Zaakceptuj przeniesienie": "Accept transfer",
  "Odrzuć przeniesienie": "Decline transfer",
  Edytuj: "Edit",

  // --- pozostałe ---
  "Karta podarunkowa": "Gift card",
  "Punkt odbioru": "Store",
  Sposób: "Method",
  "Ilość:": "Quantity:",
  "Nie udało się przenieść Twojego koszyka":
    "Something went wrong when we tried to transfer your cart",
  "Zobacz koszyk": "View cart",

  // --- przenoszenie zamówienia (linki z e-maila) ---
  Wróć: "Back",
  "Język:": "Language:",
  "Prośba o przeniesienie zamówienia": "Transfer request for order",
  "Ktoś poprosił o przeniesienie własności Twojego zamówienia":
    "You have received a request to transfer ownership of your order",
  "Jeśli się na to zgadzasz, zatwierdź przeniesienie przyciskiem poniżej.":
    "If you agree, approve the transfer using the button below.",
  "Po zaakceptowaniu nowy właściciel przejmuje wszystkie uprawnienia i obowiązki związane z tym zamówieniem.":
    "Once accepted, the new owner takes over all rights and responsibilities for this order.",
  "Jeśli nie rozpoznajesz tej prośby albo chcesz zachować zamówienie, nie musisz nic robić.":
    "If you do not recognise this request, or wish to keep the order, no action is needed.",
  "Zamówienie przeniesione": "Order transferred",
  Zamówienie: "Order",
  "zostało przeniesione na nowego właściciela.":
    "has been transferred to the new owner.",
  "zostało odrzucone.": "has been declined.",
  "Nie udało się zaakceptować przeniesienia. Spróbuj ponownie.":
    "The transfer could not be accepted. Please try again.",
  "Nie udało się odrzucić przeniesienia. Spróbuj ponownie.":
    "The transfer could not be declined. Please try again.",
  "Przeniesienie odrzucone": "Transfer declined",
  "Treść błędu:": "Error message:",

  Tylko: "Only",
  "Uwaga:": "Attention:",
  "Tylko do testów": "For testing purposes only.",

  // --- koszyk ---
  Produkt: "Product",
  Ilość: "Quantity",
  Cena: "Price",
  Razem: "Total",
  Rozmiar: "Size",
  Usuń: "Remove",
  Podsumowanie: "Summary",
  "Wartość produktów": "Subtotal",
  Dostawa: "Shipping",
  Rabat: "Discount",
  "W tym VAT": "incl. VAT",
  "Razem do zapłaty": "Order total",
  "(bez wysyłki)": "(excl. shipping)",
  "Przejdź do kasy": "Go to checkout",
  "Przejdź do koszyka": "Go to cart",
  "Przejdź do listy produktów": "Go to all products",
  "Twój koszyk jest pusty.": "Your cart is empty.",
  "Nie masz jeszcze nic w koszyku. Zajrzyj do naszych projektów — może coś Cię przekona.":
    "There is nothing in your cart yet. Have a look at our designs — something may win you over.",
  "Zobacz produkty": "Browse products",
  "Masz już konto?": "Already have an account?",
  "Zaloguj się — szybciej wypełnisz zamówienie.":
    "Sign in — checkout will be quicker.",
  "Mam kod rabatowy": "I have a promo code",
  Zastosuj: "Apply",
  "Zastosowane rabaty:": "Applied discounts:",
  "Usuń kod rabatowy z zamówienia": "Remove promo code from order",

  // --- zamawianie ---
  "Wróć do koszyka": "Back to cart",
  "Adres dostawy": "Shipping address",
  "Adres do faktury": "Billing address",
  "Adres do faktury taki sam jak adres dostawy":
    "Billing address same as shipping address",
  "Adres do faktury taki sam jak adres dostawy.":
    "Billing address is the same as the shipping address.",
  "Dane kontaktowe": "Contact details",
  "Przejdź do dostawy": "Continue to delivery",
  "Przejdź do płatności": "Continue to payment",
  "Przejdź do podsumowania": "Continue to review",
  "Sposób dostawy": "Delivery method",
  "Jak dostarczyć zamówienie?": "How should we deliver your order?",
  "Odbiór osobisty": "Pick up in person",
  "Wybierz punkt w pobliżu": "Choose a pickup point near you",
  Płatność: "Payment",
  "Metoda płatności": "Payment method",
  "Szczegóły płatności": "Payment details",
  "Wybierz metodę płatności": "Select a payment method",
  "Podaj dane karty:": "Enter your card details:",
  "Pojawi się kolejny krok": "Another step will appear",
  "Twój koszyk": "Your cart",
  Zmień: "Edit",
  "Wybierz adres": "Choose an address",
  "Złóż zamówienie": "Place order",
  'Klikając „Złóż zamówienie" potwierdzasz, że zapoznałeś się z regulaminem sklepu, warunkami sprzedaży i zasadami zwrotów oraz z polityką prywatności Ateisci.com.':
    "By clicking Place order you confirm that you have read the store terms, the sales and returns policy, and the Ateisci.com privacy policy.",

  // --- formularze adresowe ---
  Imię: "First name",
  Nazwisko: "Last name",
  "Ulica i numer": "Address",
  "Mieszkanie / lokal": "Apartment, suite, etc.",
  "Firma (opcjonalnie)": "Company (optional)",
  "Kod pocztowy": "Postal code",
  Miejscowość: "City",
  Województwo: "Province",
  Telefon: "Phone",
  "E-mail": "Email",
  Hasło: "Password",
  "Imię i nazwisko": "Full name",
  "Obecne hasło": "Current password",
  "Nowe hasło": "New password",
  "Powtórz hasło": "Confirm password",
  "Podaj poprawny adres e-mail.": "Enter a valid email address.",
  Zapisz: "Save",
  Anuluj: "Cancel",

  // --- zamówienia ---
  "Szczegóły zamówienia": "Order details",
  "Podsumowanie zamówienia": "Order summary",
  "Numer zamówienia": "Order number",
  "Numer zamówienia:": "Order number:",
  "Data zamówienia:": "Order date:",
  "Status realizacji:": "Fulfilment status:",
  "Status płatności:": "Payment status:",
  "Potwierdzenie zamówienia wysłaliśmy na adres":
    "We have sent the order confirmation to",
  "Dziękujemy!": "Thank you!",
  "Zamówienie zostało złożone.": "Your order has been placed.",
  "Wróć do listy zamówień": "Back to orders",
  "Potrzebujesz pomocy?": "Need help?",
  Kontakt: "Contact",
  "Zwroty i wymiany": "Returns & exchanges",

  // --- konto ---
  "Witaj ponownie": "Welcome back",
  "Zaloguj się": "Sign in",
  "Zaloguj się, żeby szybciej składać zamówienia i śledzić ich status.":
    "Sign in to order faster and track your orders.",
  "Nie masz konta?": "No account yet?",
  "Załóż konto": "Create an account",
  "Załóż konto w Ateisci.com": "Create an Ateisci.com account",
  "Konto pozwala śledzić zamówienia i zapisać adres wysyłki na przyszłość.":
    "An account lets you track orders and save your shipping address for next time.",
  "Zakładając konto akceptujesz": "By creating an account you accept the",
  "politykę prywatności": "privacy policy",
  oraz: "and the",
  regulamin: "terms of use",
  "Dane osobowe": "Personal details",
  Adresy: "Addresses",
  "Adresy dostawy": "Shipping addresses",
  Zamówienia: "Orders",
  "Wyloguj się": "Log out",
  "Ostatnie zamówienia": "Recent orders",
  "Brak zamówień": "No orders yet",
  "Data złożenia": "Order date",
  "Pusto tutaj": "Nothing here yet",
  "Nie masz jeszcze żadnych zamówień.": "You have not placed any orders yet.",
  "Masz pytania?": "Got questions?",
  "Nowy adres": "New address",
  "Dodaj adres": "Add address",
  "Edytuj adres": "Edit address",
  "Ze względów bezpieczeństwa hasło nie jest wyświetlane":
    "The password is not shown for security reasons",

  // --- błędy i stany puste ---
  "Nie ma takiej strony": "Page not found",
  "Strona, której szukasz, nie istnieje.":
    "The page you are looking for does not exist.",
  "Ten koszyk już nie istnieje. Wyczyść ciasteczka i spróbuj ponownie.":
    "This cart no longer exists. Clear your cookies and try again.",
  "Wróć na stronę główną": "Back to homepage",

  // --- tytuły i opisy stron ---
  Zamawianie: "Checkout",
  "Zamówienie potwierdzone": "Order confirmed",
  "Nie znaleziono strony": "Page not found",
  "Podgląd koszyka": "View your cart",
  "Przegląd aktywności na koncie.": "Overview of your account activity.",
  "Zakup zakończył się powodzeniem.": "Your purchase was successful.",
  "Podgląd i edycja danych Twojego konta.": "View and edit your account details.",
  "Zaloguj się na swoje konto w Ateisci.com.": "Sign in to your Ateisci.com account.",
  "Ateisci.com — koszulki z autorskimi nadrukami w tematyce ateistycznej. Rozmiary S-XXL, wysyłka kurierem lub do Paczkomatu.":
    "Ateisci.com — t-shirts with original atheist-themed prints. Sizes S-XXL, courier or parcel-locker delivery.",

  // --- przełącznik języka ---
  "Zmień język na angielski": "Switch to English",
  "Zmień język na polski": "Switch to Polish",
}

export type Lang = "pl" | "en"

/** Zwraca funkcję tłumaczącą dla danego języka. Dla `pl` to identyczność. */
export const makeTranslator =
  (lang: Lang) =>
  (text: string): string =>
    lang === "pl" ? text : EN[text] ?? text
